import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertCircle,
  ArrowLeftRight,
  CheckCircle2,
  ClipboardCheck,
} from 'lucide-react'
import { type LucideIcon } from 'lucide-react'
import {
  useInvoiceQueues,
  useReconciliationQueue,
  type ClientCount,
  type UnmatchedClientGroup,
} from '@/hooks/useDashboard'
import { ThroughputChart } from './ThroughputChart'
import { AIAccuracyCard } from './AIAccuracyCard'
import { ReconciliationStatsCard } from './ReconciliationStatsCard'
import { ClientsSummaryTable } from './ClientsSummaryTable'

const DAYS_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
]

export function DashboardPage() {
  const [days, setDays] = useState('30')
  const daysNum = parseInt(days, 10)

  return (
    <div className="page-shell space-y-6 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-950">Dashboard</h1>
          <p className="text-sm text-neutral-500">Across all clients</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DAYS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      <ActionCardsRow />

      <ThroughputChart days={daysNum} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <AIAccuracyCard days={daysNum} />
        <ReconciliationStatsCard days={daysNum} />
      </div>

      <ClientsSummaryTable days={daysNum} />
    </div>
  )
}

function ActionCardsRow() {
  const queues = useInvoiceQueues()
  const recon = useReconciliationQueue()
  const navigate = useNavigate()

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <ActionCard
        icon={ClipboardCheck}
        accent="amber"
        title="Pending review"
        count={queues.data?.pending_review_total ?? 0}
        loading={queues.isLoading}
        topClients={queues.data?.pending_review_top ?? []}
        cta="Review now"
        onClick={() =>
          navigate('/invoices/processing', { state: { triggerReviewAll: true } })
        }
      />
      <ActionCard
        icon={CheckCircle2}
        accent="blue"
        title="Ready to export"
        count={queues.data?.ready_to_export_total ?? 0}
        loading={queues.isLoading}
        topClients={queues.data?.ready_to_export_top ?? []}
        cta="Open invoices"
        onClick={() => navigate('/invoices/processing?status=reviewed')}
      />
      <ActionCard
        icon={AlertCircle}
        accent="red"
        title="Failed"
        count={queues.data?.failed_total ?? 0}
        loading={queues.isLoading}
        topClients={queues.data?.failed_top ?? []}
        cta="Open invoices"
        onClick={() => navigate('/invoices/processing')}
      />
      <UnmatchedActionCard
        count={recon.data?.unmatched_total ?? 0}
        loading={recon.isLoading}
        groups={recon.data?.unmatched_top ?? []}
      />
    </div>
  )
}

interface ActionCardProps {
  icon: LucideIcon
  accent: 'amber' | 'blue' | 'red' | 'violet'
  title: string
  count: number
  loading: boolean
  topClients: ClientCount[]
  cta: string
  onClick: () => void
}

const ACCENT_CLASSES: Record<ActionCardProps['accent'], string> = {
  amber: 'bg-amber-50 text-amber-700',
  blue: 'bg-blue-50 text-blue-700',
  red: 'bg-red-50 text-red-700',
  violet: 'bg-violet-50 text-violet-700',
}

function ActionCard({
  icon: Icon,
  accent,
  title,
  count,
  loading,
  topClients,
  cta,
  onClick,
}: ActionCardProps) {
  const isEmpty = !loading && count === 0
  return (
    <Card className="flex flex-col">
      <CardContent className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between">
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-lg ${ACCENT_CLASSES[accent]}`}
          >
            <Icon className="h-5 w-5" />
          </div>
          {!loading && (
            <span className="text-3xl font-semibold tabular-nums text-neutral-950">{count}</span>
          )}
          {loading && <span className="h-8 w-12 animate-pulse rounded bg-neutral-100" />}
        </div>

        <div>
          <div className="text-sm font-medium text-neutral-950">{title}</div>
          {isEmpty ? (
            <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> All clear
            </div>
          ) : topClients.length > 0 ? (
            <ul className="mt-1 space-y-0.5 text-xs text-neutral-500">
              {topClients.slice(0, 3).map((c) => (
                <li key={c.client_id} className="flex justify-between">
                  <span className="truncate">{c.client_name}</span>
                  <span className="tabular-nums">{c.count}</span>
                </li>
              ))}
              {topClients.length > 3 && (
                <li className="text-neutral-400">+{topClients.length - 3} more</li>
              )}
            </ul>
          ) : (
            <div className="mt-1 text-xs text-neutral-400">—</div>
          )}
        </div>

        <Button
          variant={isEmpty ? 'outline' : 'default'}
          size="sm"
          className={
            isEmpty
              ? 'mt-auto'
              : 'mt-auto bg-[#0000ff] text-white hover:bg-[#3333ff]'
          }
          onClick={onClick}
          disabled={isEmpty}
        >
          {cta}
        </Button>
      </CardContent>
    </Card>
  )
}

interface UnmatchedActionCardProps {
  count: number
  loading: boolean
  groups: UnmatchedClientGroup[]
}

const UNMATCHED_CLIENT_LIMIT = 3

function UnmatchedActionCard({ count, loading, groups }: UnmatchedActionCardProps) {
  const isEmpty = !loading && count === 0
  const visibleGroups = groups.slice(0, UNMATCHED_CLIENT_LIMIT)
  const overflow = Math.max(0, groups.length - visibleGroups.length)

  return (
    <Card className="flex flex-col">
      <CardContent className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between">
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-lg ${ACCENT_CLASSES.violet}`}
          >
            <ArrowLeftRight className="h-5 w-5" />
          </div>
          {!loading && (
            <span className="text-3xl font-semibold tabular-nums text-neutral-950">{count}</span>
          )}
          {loading && <span className="h-8 w-12 animate-pulse rounded bg-neutral-100" />}
        </div>

        <div className="flex-1">
          <div className="text-sm font-medium text-neutral-950">Unmatched bank txns</div>
          {isEmpty ? (
            <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> All clear
            </div>
          ) : visibleGroups.length === 0 ? (
            <div className="mt-1 text-xs text-neutral-400">—</div>
          ) : (
            <ul className="mt-2 space-y-2 text-xs">
              {visibleGroups.map((g) => (
                <li key={g.client_id}>
                  <div className="flex justify-between text-neutral-700">
                    <span className="truncate font-medium">{g.client_name}</span>
                    <span className="tabular-nums text-neutral-500">{g.count}</span>
                  </div>
                  <ul className="mt-0.5 space-y-0.5 pl-2 text-neutral-500">
                    {g.statements.map((s) => {
                      const qs = new URLSearchParams({ client: g.client_id })
                      if (s.account_id != null) qs.set('account', String(s.account_id))
                      qs.set('statement', s.statement_id)
                      return (
                        <li key={s.statement_id} className="flex justify-between">
                          <Link
                            to={`/reconciliation?${qs.toString()}`}
                            className="truncate text-[#0000ff] hover:underline"
                            title={s.statement_name}
                          >
                            {s.statement_name}
                          </Link>
                          <span className="ml-2 shrink-0 tabular-nums">{s.count}</span>
                        </li>
                      )
                    })}
                  </ul>
                </li>
              ))}
              {overflow > 0 && (
                <li className="text-neutral-400">+{overflow} more</li>
              )}
            </ul>
          )}
        </div>

        {isEmpty && (
          <Button variant="outline" size="sm" className="mt-auto" disabled>
            Open reconciliation
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

export default DashboardPage
